// src/components/pages/ScratchDashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../layout/AppShell";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import Icon from "../ui/Icon";
import Spinner from "../ui/Spinner";
import StateSelector from "../scratch/StateSelector";
import ScratchScore from "../scratch/ScratchScore";
import ResponsibleGamblingDisclaimer from "../scratch/ResponsibleGamblingDisclaimer";
import PaidAccessCard from "../scratch/PaidAccessCard";
import ScratchTierBadge from "../scratch/ScratchTierBadge";
import ScratchRiskValueReport from "../scratch/ScratchRiskValueReport";
import {
  getScratchAccess,
  getStates,
  getRankedGames,
  formatMoney,
  formatRatioPct,
} from "../../services/scratchService";

const PRICE_BANDS = [
  { key: "ALL", label: "All prices", min: undefined, max: undefined },
  { key: "1-5", label: "$1–$5", min: 1, max: 5 },
  { key: "10-20", label: "$10–$20", min: 10, max: 20 },
  { key: "30+", label: "$30+", min: 30, max: undefined },
];

function TrendBadge({ trend }) {
  if (trend === "UP") return <Badge variant="gold" icon="arrow-up">Rising</Badge>;
  if (trend === "DOWN") return <Badge variant="red" icon="arrow-down">Falling</Badge>;
  return null;
}

function GameRow({ game, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(game.id)}
      className="w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all text-left"
      style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}
    >
      <ScratchScore score={game.scratchScore} size="sm" showLabel={false} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate" style={{ color: "var(--txt)" }}>
            {game.gameName || `Game #${game.gameNumber}`}
          </span>
          <TrendBadge trend={game.scoreTrend} />
        </div>
        <div className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
          {formatMoney(game.ticketPrice)} · top prize {formatMoney(game.topPrizeAmount)} ·{" "}
          {formatRatioPct(game.topPrizeRemainingRatio)} of top prizes remain
        </div>
      </div>
      <Icon name="arrow-right" size={16} className="shrink-0" />
    </button>
  );
}

export default function ScratchDashboard() {
  const navigate = useNavigate();

  // Access matrix from the backend — drives every gating decision below.
  // access === null = still loading; access.hasAccess === false = paywall.
  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);

  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [state, setState] = useState("");
  const [band, setBand] = useState("ALL");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 1) Access first. Everything else is gated on it.
  useEffect(() => {
    (async () => {
      setAccessLoading(true);
      const a = await getScratchAccess();
      setAccess(a);
      setAccessLoading(false);
    })();
  }, []);

  // 2) Only load states / games if user has access.
  useEffect(() => {
    if (!access?.hasAccess) return;
    (async () => {
      setStatesLoading(true);
      const list = await getStates();
      setStates(list);
      if (list.length > 0) setState(list[0].stateCode);
      setStatesLoading(false);
    })();
  }, [access?.hasAccess]);

  const loadGames = useCallback(async () => {
    if (!access?.hasAccess || !state) return;
    setLoading(true);
    setError("");
    const b = PRICE_BANDS.find((x) => x.key === band) || PRICE_BANDS[0];
    const list = await getRankedGames(state, { minPrice: b.min, maxPrice: b.max });
    setGames(list);
    if (list.length === 0) setError("No games found for this state and price range.");
    setLoading(false);
  }, [access?.hasAccess, state, band]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const best = useMemo(() => games.slice(0, 5), [games]);
  const avoid = useMemo(
    () => [...games].sort((a, b) => Number(a.scratchScore) - Number(b.scratchScore)).slice(0, 5),
    [games]
  );

  const isMultiStateTier = access?.tier === "PRO" || access?.tier === "ELITE";
  const canRunReport = access?.riskValueReports === true;

  // ---- Paywall short-circuit ----
  if (accessLoading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-24" style={{ color: "var(--txt-2)" }}>
          <Spinner size={22} />
          <span className="ml-2 text-[13px]">Loading…</span>
        </div>
      </AppShell>
    );
  }

  if (!access?.hasAccess) {
    return (
      <AppShell>
        <div className="px-4 py-8 max-w-[820px] mx-auto space-y-6">
          <header>
            <h1 className="font-semibold flex items-center gap-2" style={{ fontSize: 24, color: "var(--txt)" }}>
              <Icon name="ticket" size={22} />
              Scratch-Off Intelligence
            </h1>
            <p className="text-[13px] mt-1" style={{ color: "var(--txt-2)" }}>
              Games ranked by remaining prize strength, from public lottery data.
            </p>
          </header>
          <PaidAccessCard tier={access?.tier} />
          <ResponsibleGamblingDisclaimer />
        </div>
      </AppShell>
    );
  }

  // ---- Subscribed user — full dashboard ----
  return (
    <AppShell>
      <div className="px-4 py-8 space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1
                className="font-semibold flex items-center gap-2"
                style={{ fontSize: 24, color: "var(--txt)" }}
              >
                <Icon name="ticket" size={22} />
                Scratch-Off Intelligence
              </h1>
              <ScratchTierBadge tier={access.tier} />
            </div>
            <p className="text-[13px]" style={{ color: "var(--txt-2)" }}>
              Games ranked by remaining prize strength, from public lottery data.
              {access.currentCredits >= 0 && (
                <>
                  {" "}
                  <span style={{ color: "var(--txt-3)" }}>
                    · {access.currentCredits} credit{access.currentCredits === 1 ? "" : "s"} available
                  </span>
                </>
              )}
            </p>
          </div>
          <Button icon="refresh" size="sm" variant="ghost" onClick={loadGames} loading={loading}>
            Refresh
          </Button>
        </header>

        <div className="space-y-3">
          <StateSelector
            states={states}
            value={state}
            onChange={setState}
            loading={statesLoading}
          />
          {!isMultiStateTier && states.length > 1 && (
            <p className="text-[11.5px] flex items-center gap-1.5" style={{ color: "var(--txt-3)" }}>
              <Icon name="info-circle" size={12} />
              The Plus tier is limited to one state at a time.{" "}
              <button
                type="button"
                onClick={() => navigate("/plans")}
                className="underline"
                style={{ color: "var(--teal)" }}
              >
                Upgrade to Pro
              </button>{" "}
              for multi-state coverage.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {PRICE_BANDS.map((b) => {
              const active = b.key === band;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setBand(b.key)}
                  className="px-3 py-1.5 rounded-full text-[12px] font-medium transition-all"
                  style={{
                    background: active ? "var(--teal-dim)" : "var(--surf-2)",
                    border: `1px solid ${active ? "var(--teal-border)" : "var(--border)"}`,
                    color: active ? "var(--teal)" : "var(--txt-2)",
                  }}
                >
                  {b.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16" style={{ color: "var(--txt-2)" }}>
            <Spinner size={20} />
            <span className="ml-2 text-[13px]">Scoring games…</span>
          </div>
        ) : error ? (
          <Card variant="soft">
            <p className="text-[13px] text-center" style={{ color: "var(--txt-2)" }}>
              {error}
            </p>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="trophy" size={18} className="text-[var(--gold)]" />
                <h2 className="font-semibold text-[15px]" style={{ color: "var(--txt)" }}>
                  Better relative value today
                </h2>
              </div>
              <div className="space-y-2">
                {best.map((g) => (
                  <GameRow key={g.id} game={g} onOpen={(id) => navigate(`/scratch/games/${id}`)} />
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Icon name="ticket-off" size={18} className="text-[var(--txt-3)]" />
                <h2 className="font-semibold text-[15px]" style={{ color: "var(--txt)" }}>
                  Lower remaining prize strength
                </h2>
              </div>
              <div className="space-y-2">
                {avoid.map((g) => (
                  <GameRow key={g.id} game={g} onOpen={(id) => navigate(`/scratch/games/${id}`)} />
                ))}
              </div>
            </section>
          </div>
        )}

        {/* PRO+ only: alerts shortcut */}
        {access.alertsAndTrends && (
          <Card variant="soft">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
                  Scratch-off alerts
                </div>
                <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
                  Get notified when a top prize is claimed, when a game's score moves,
                  or when a new game launches.
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => navigate("/scratch/alerts")}>
                <Icon name="bell" size={14} />
                Open alerts
              </Button>
            </div>
          </Card>
        )}

        {/* PLUS-only soft upsell for alerts */}
        {!access.alertsAndTrends && (
          <Card variant="soft">
            <div className="flex items-start gap-3 flex-wrap">
              <Icon name="bell" size={18} style={{ color: "var(--txt-3)", marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
                  Alerts and trend charts are a Pro feature.
                </div>
                <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
                  Upgrade to Pro to receive notifications when prize remainders shift
                  and to see each game's score history over time.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/plans")}>
                Upgrade
              </Button>
            </div>
          </Card>
        )}

        {/* ELITE-only: the premium Risk & Value Report block */}
        {canRunReport ? (
          <ScratchRiskValueReport
            state={state}
            minPrice={PRICE_BANDS.find((x) => x.key === band)?.min}
            maxPrice={PRICE_BANDS.find((x) => x.key === band)?.max}
            currentCredits={access.currentCredits}
            onCreditChange={(next) =>
              setAccess((prev) => (prev ? { ...prev, currentCredits: next } : prev))
            }
          />
        ) : (
          <Card variant="soft">
            <div className="flex items-start gap-3 flex-wrap">
              <Icon name="file-text" size={18} style={{ color: "var(--txt-3)", marginTop: 2 }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px]" style={{ color: "var(--txt)" }}>
                  Scratch-Off Risk &amp; Value Report — Elite only
                </div>
                <p className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
                  Generate a detailed report ranking games by relative value, based on
                  public prize-inventory data. Available on the Elite tier; each run
                  spends 1 credit.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/plans")}>
                See Elite
              </Button>
            </div>
          </Card>
        )}

        <ResponsibleGamblingDisclaimer />
      </div>
    </AppShell>
  );
}
